'use client';

import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {confirmAlert} from 'react-confirm-alert';

/* MODAL */
import {useModal} from 'hooks/modalContext';
import CloseBtn from 'components/util/CloseModal';

/* JS */
import * as Server from 'assets/js/Server';
import * as Common from 'assets/js/Common';

/* DatePicker */
import dayjs from 'dayjs';
import moment from 'moment';
import {DemoContainer} from '@mui/x-date-pickers/internals/demo';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import * as XLSX from "xlsx";
import useDidMountEffect from "../../assets/js/useDidMountEffect";

function actionCellRenderer() {
  return <button className={'action-delete'} data-action='delete'>DELETE</button>;
}

const ManagementModal = props => {
  const fileRef = useRef();
  const { closeModal } = useModal();

  const [item, setItem] = useState({
    custMstSeq: 0,
    custName: '',
    lat: '',
    lon: '',
    ipAddr: '',
    inOutNfcKey: '',
    workForm: '',
    workCnt: 0,
    workTime: 0,
    contractArea: 0,
    contractFloor: 0,
    contractStartDt: '',
    contractEndDt: '',
    contractManager: '',
    contractManagerPhone: '',
    custImg: '',
    fkProductType: '',
    fkClientTypeSeq: '',
    custDetailDTOList: [],
    userId: ''
  });
  const [productType, setProductType] = useState([]);
  const [clientType, setClientType] = useState([]);
  const [isNfcCheck, setIsNfcCheck] = useState(false);
  const [isIdCheck, setIsIdCheck] = useState(false); // ID 체크 상태 추가
  const [isSubmit, setIsSubmit] = useState(false);
  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    Server.sendGet('product/type', {}, getProductTypeList).then();
    Server.sendGet('client/type', {}, getClientTypeList)
  }, []);

  useEffect(() => {
    if (!props.props?.isRegi) {
      setItem(props.props?.data.item);
      setRowData(props.props?.data.item?.custDetailDTOList);
      setIsIdCheck(true); // 수정 시에는 이미 ID가 있으므로 true
    }
  }, [props]);

  const getProductTypeList = res => {
    setProductType(res['resultList']);
  };

  const getClientTypeList = res => {
    setClientType(res['resultList']);
  }

  const validation = () => {
    const clientMsgArr = [
      'Client Name required.',
      'Client Id required.', // 추가
      'Product Type required.',
      'NFC Key required.',
      'The number of people required.',
      'Contract Square Feet required.',
      'Contract Floor required.',
      'Contract Start Date required.',
      'Contract End Date required.',
      'Client Manager required.',
      'Client Manager Phone required.',
    ];
    const clientColumn = [
      'custName',
      'userId', // 추가
      'fkProductType',
      'inOutNfcKey',
      'workCnt',
      'contractArea',
      'contractFloor',
      'contractStartDt',
      'contractEndDt',
      'contractManager',
      'contractManagerPhone',
    ];

    for (let i = 0; i < clientColumn.length; i++) {
      if (Common.isEmpty(item[clientColumn[i]])) {
        Common.errorMsg(clientMsgArr[i]);

        return false;
      }
    }

    if (props.props?.isRegi && !isNfcCheck) {
      Common.errorMsg('NFC Key verification required.');

      return false;
    }

    // Client ID 중복체크 확인
    if (props.props?.isRegi && !isIdCheck) {
      Common.errorMsg('Client ID verification required.');

      return false;
    }

    return true;
  };

  const setClient = () => {
    if (validation() && !isSubmit) {
      confirmAlert({
        title: 'Are you sure you want to save?',
        buttons: [{
          label: 'Yes',
          onClick: () => {
            setIsSubmit(true);

            const obj = {
              custDetailDTOList: rowData
            };

            const request = props.props?.isRegi ? Server.sendPost : Server.sendPut;

            request('client/management', { ...item, ...obj }, setClientCallback).then();
          }
        }, {
          label: 'No'
        }]
      });
    }
  };

  const setClientCallback = () => {
    setIsSubmit(false);

    props.props.data.callback();

    Common.alertSuccess();

    closeModal();
  };

  const handleStartDateChange = date => {
    const isBeforeDate = moment(item.contractEndDt).isBefore(moment(date['$d']).format('YYYY-MM-DD'));

    setItem(() => {
      return {
        ...item,
        contractStartDt: moment(date['$d']).format('YYYY-MM-DD'),
        contractEndDt: isBeforeDate ? moment(date['$d']).format('YYYY-MM-DD') : item.contractEndDt,
      }
    });
  };

  const handleEndDateChange = date => {
    const isBeforeDate = moment(moment(date['$d']).format('YYYY-MM-DD')).isBefore(item.contractStartDt);

    setItem(() => {
      return {
        ...item,
        contractStartDt: isBeforeDate ? moment(date['$d']).format('YYYY-MM-DD') : item.contractStartDt,
        contractEndDt: moment(date['$d']).format('YYYY-MM-DD'),
      }
    });
  };

  const nfcCheck = () => {
    if (isNfcCheck) return;

    if (Common.isEmpty(item.inOutNfcKey)) {
      Common.errorMsg('NFC Key required.');
      return;
    }

    Server.sendGet('client/management/nfc', {
      inOutNfcKey: item?.inOutNfcKey
    }, nfcCheckCallback).then();
  };

  const nfcCheckCallback = res => {
    if (res?.['resultCount'] === 0) {
      Common.errorMsg('Available.');
      setIsNfcCheck(true);
    } else {
      Common.errorMsg('NFC Key existing.');
      setItem(prev => ({ ...prev, inOutNfcKey: '' }));
      setIsNfcCheck(false);
    }
  };

  // Client ID 중복 체크 함수 추가
  const checkClientId = () => {
    if (isIdCheck) return;

    if (Common.isEmpty(item.userId)) {
      Common.errorMsg('Client ID required.');
      return;
    }

    // 서버 URL 비워둔 채로 호출부 구성
    Server.sendGet('user/id', {
      userId: item.userId
    }, checkClientIdCallback).then();
  };

  const checkClientIdCallback = res => {
    const result = res['resultObject'];

    if (result['checkUserId'] === 0) {
      Common.errorMsg('Available Client ID.');
      setIsIdCheck(true);
    } else {
      Common.errorMsg('Existing Client ID.');
      setIsIdCheck(false);
      setItem(prev => ({ ...prev, userId: '' }));
    }
  };

  const onClickImgFile = () => {
    fileRef.current?.click();
  };

  const fileToUrl = e => {
    const file = e.target.files[0];

    if (!file) return;

    const maxSize = 100 * 1024 * 1024; // 100MB

    if (file.size > maxSize) {
      Common.errorMsg('File size cannot exceed 100MB.');
      e.target.value = '';
      return;
    }

    if (!file.type.includes('image')) {
      Common.errorMsg('This is not allowed file type.');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    Server.fileUpload('file/client/s3upload', formData).then(res => {
      if (res.data['resultObject']) {
        setItem(prev => ({
          ...prev,
          custImg: res.data['resultObject']?.['s3Url'],
        }));
      } else {
        Common.errorMsg('Upload failed. Please try again in a moment.');
      }
    }).catch(() => {
      Common.errorMsg('Upload failed. Please try again in a moment.');
    }).finally(() => {
      e.target.value = '';
    });
  };

  const deleteImg = () => {
    confirmAlert({
      title: 'Are you sure you want to delete?',
      buttons: [{
        label: 'Yes',
        onClick: () => {
          setItem(prev => ({ ...prev, custImg: '' }));
        }
      }, {
        label: 'No'
      }]
    });
  }

  //Excel Upload
  const ExcelInput = useRef();
  const [data, setData] = useState([]);
  const [hasFile, setHasFile] = useState(false);
  const [hasSend, setHasSend] = useState(null);

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];

    if (file) {
      setHasFile(true);
      const render = new FileReader();
      render.onload = (evt) => {
        const bstr = evt.target?.result;
        const wb = XLSX.read(bstr, {type: 'binary'});
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        setData(data);
        setHasSend(JSON.stringify(data, null, 2));
      };
      render.readAsBinaryString(file);
    }
  }, []);

  useDidMountEffect(() => {
    setRowData(data);
  }, [data])

  return (
    <>
      <CloseBtn onClick={closeModal}/>

      <div className='modal-box'>
        <div className='inner' style={{ height:'800px' }}>
          <Title>
            <TitHead>
              <i/>
              <h4>Client {props.props?.isRegi ? 'Registration' : 'Modify'}</h4>
            </TitHead>
          </Title>
          <ModalForm>
            <FormIn>
              <FormInWrap>
                <label><span>* </span>Client Name</label>
                <FormInput
                  type='text'
                  id='custName'
                  name='custName'
                  value={item?.custName || ''}
                  onChange={e => {
                    setItem(prev => ({ ...prev, custName: e.target.value }));
                  }}
                />
              </FormInWrap>
              <FormInWrap>
                <label><span>* </span>Product Type</label>
                <select
                  name='productType'
                  id='productType'
                  value={item?.fkProductType || ''}
                  onChange={e => {
                    setItem(prev => ({ ...prev, fkProductType: e.target.value }));
                  }}
                >
                  <option value={''}>-- Select --</option>
                  {productType.map(v => (
                    <option key={v.productTypeMstSeq} value={v.productTypeMstSeq}>{v.productType}</option>
                  ))}
                </select>
              </FormInWrap>
            </FormIn>

            <FormIn>
              <FormInWrap>
                <label><span>* </span>Client Type</label>
                <select
                  name='clientType'
                  id='clientType'
                  value={item?.fkClientTypeSeq || ''}
                  onChange={e => {
                    setItem(prev => ({ ...prev, fkClientTypeSeq: e.target.value }));
                  }}
                >
                  <option value={''}>-- Select --</option>
                  {clientType.map(v => (
                    <option key={v.clientTypeSeq} value={v.clientTypeSeq}>{v.clientTypeName}</option>
                  ))}
                </select>
              </FormInWrap>
              {/* 추가된 Client ID 필드 */}
              <FormInWrap>
                <label><span>* </span>Client Id</label>
                <FormInput
                  style={{ width: props.props?.isRegi ? 'calc(55% - 20px)' : 'calc(70% - 10px)' }}
                  type='text'
                  id='userId'
                  name='userId'
                  value={item?.userId || ''}
                  readOnly={!props.props?.isRegi}
                  onChange={e => {
                    setItem(prev => ({ ...prev, userId: e.target.value }));
                    setIsIdCheck(false);
                  }}
                />
                {props.props?.isRegi &&
                  <Btn
                    style={{
                      flex: 'none',
                      width: '15%',
                      fontSize: '16px',
                      marginLeft: '10px',
                      background: !isIdCheck ? '#0072b9' : '#a4a4a4'
                    }}
                    onClick={checkClientId}
                  >Check</Btn>
                }
              </FormInWrap>
            </FormIn>

            <FormIn>
              <FormInWrap>
                <label><span>* </span>Commute<br/>NFC Key</label>
                <FormInput
                  style={{ width: props.props?.isRegi ? 'calc(55% - 20px)' : 'calc(70% - 10px)' }}
                  type='text'
                  id='inOutNfcKey'
                  name='inOutNfcKey'
                  value={item?.inOutNfcKey || ''}
                  readOnly={!props.props?.isRegi}
                  onChange={e => {
                    setItem(prev => ({ ...prev, inOutNfcKey: e.target.value }));
                    setIsNfcCheck(false);
                  }}
                />
                {props.props?.isRegi &&
                  <Btn
                    style={{
                      flex: 'none',
                      width: '15%',
                      fontSize: '16px',
                      marginLeft: '10px',
                      background: !isNfcCheck ? '#0072b9' : '#a4a4a4'
                    }}
                    onClick={nfcCheck}
                  >Check</Btn>
                }
              </FormInWrap>
              <FormInWrap>
                <label><span>* </span>The number of people</label>
                <FormInput
                  type='text'
                  id='workCnt'
                  name='workCnt'
                  value={item?.workCnt || ''}
                  onChange={e => {
                    e.target.value = Common.onlyNumber(e.target.value);
                    setItem(prev => ({ ...prev, workCnt: e.target.value }));
                  }}
                />
              </FormInWrap>
            </FormIn>
            <FormIn>
              <FormInWrap>
                <label><span>* </span>Contract Square Feet</label>
                <FormInput
                  type='text'
                  id='contractArea'
                  name='contractArea'
                  value={item?.contractArea || ''}
                  onChange={e => {
                    e.target.value = Common.onlyNumber(e.target.value);
                    setItem(prev => ({ ...prev, contractArea: e.target.value }));
                  }}
                />
              </FormInWrap>
              <FormInWrap>
                <label><span>* </span>Contract Floor</label>
                <FormInput
                  type='text'
                  id='contractFloor'
                  name='contractFloor'
                  value={item?.contractFloor || ''}
                  onChange={e => {
                    e.target.value = Common.onlyNumber(e.target.value);
                    setItem(prev => ({ ...prev, contractFloor: e.target.value }));
                  }}
                />
              </FormInWrap>
            </FormIn>
            <FormIn>
              <FormInWrap>
                <label><span>* </span>Contract Start Date</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker', 'DatePicker']}>
                    <DatePicker
                      onChangeRaw={e => e.preventDefault()}
                      format={'YYYY-MM-DD'}
                      value={item?.contractStartDt ? dayjs(item.contractStartDt) : null}
                      onChange={date => handleStartDateChange(date)}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </FormInWrap>
              <FormInWrap>
                <label><span>* </span>Contract End Date</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker', 'DatePicker']}>
                    <DatePicker
                      onChangeRaw={e => e.preventDefault()}
                      format={'YYYY-MM-DD'}
                      value={item?.contractEndDt ? dayjs(item.contractEndDt) : null}
                      onChange={date => handleEndDateChange(date)}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </FormInWrap>
            </FormIn>
            <FormIn>
              <FormInWrap>
                <label><span>* </span>Client Manager</label>
                <FormInput
                  type='text'
                  id='contractManager'
                  name='contractManager'
                  value={item?.contractManager || ''}
                  onChange={e => {
                    setItem(prev => ({ ...prev, contractManager: e.target.value }));
                  }}
                />
              </FormInWrap>
              <FormInWrap>
                <label><span>* </span>Client Manager Phone</label>
                <FormInput
                  type='text'
                  id='contractManagerPhone'
                  name='contractManagerPhone'
                  value={item?.contractManagerPhone || ''}
                  onChange={e => {
                    setItem(prev => ({ ...prev, contractManagerPhone: e.target.value }));
                  }}
                />
              </FormInWrap>
            </FormIn>
            <FormIn>
              <FormInWrap style={{ height: 'auto', width: '100%' }}>
                <label>Client Trap Location (Outdoor)</label>
                <div className='imgList'>
                  {Common.isEmpty(item?.custImg) &&
                    <div className='imgBox'>
                      <input type='file' ref={fileRef} accept='image/*' onChange={fileToUrl}/>
                      <button type='button' className='imgBtn' onClick={onClickImgFile}>
                        <img
                          src='https://mendle-s3-bucket.s3.ap-northeast-2.amazonaws.com/beauty-book/bb/review/add_img.svg'
                          alt='add'
                        />
                      </button>
                    </div>
                  }
                  {!Common.isEmpty(item?.custImg) &&
                    <div className='imgItem'>
                      <button type='button' className='imgDel' onClick={deleteImg}>
                        <img
                          src='https://mendle-s3-bucket.s3.ap-northeast-2.amazonaws.com/beauty-book/bb/review/imgClose.svg'
                          alt='delete'
                        />
                      </button>
                      <img src={item?.custImg} alt='image'/>
                    </div>
                  }
                </div>
              </FormInWrap>
            </FormIn>
          </ModalForm>
          <Title>
            <TitHead>
              <i/>
              <h4>List of quarantine zones</h4>
            </TitHead>
            <BtnDesc>
              <div className='action-btn'>
                <a href="https://ust-india.s3.ap-northeast-2.amazonaws.com/download/%E1%84%87%E1%85%A1%E1%86%BC%E1%84%8B%E1%85%A7%E1%86%A8%E1%84%92%E1%85%AA%E1%86%AF%E1%84%83%E1%85%A9%E1%86%BC+%E1%84%8B%E1%85%A5%E1%86%B8%E1%84%85%E1%85%A9%E1%84%83%E1%85%B3+%E1%84%8B%E1%85%A3%E1%86%BC%E1%84%89%E1%85%B5%E1%86%A8.xlsx"
                  className='downBtn'
                >Form Download</a>
              </div>
              <div className="upload">
                <label htmlFor='excel-read'>Read Excel</label>
                <input name='excel-read' id='excel-read' ref={ExcelInput} type='file' onChange={handleFileUpload}
                  accept=".xlsx, .xls, .csv"/>
              </div>
            </BtnDesc>
          </Title>
          <ModalForm>
            <FormIn style={{ width:'100%', maxHeight:'480px', overflowY:'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Patrol Area</th>
                    <th>Patrol Area NFC Key</th>
                    <th>Barcode Number</th>
                  </tr>
                </thead>
                <tbody>
                  {rowData?.map((item, key) => (
                    <tr key={key}>
                      <td className='center'>{item.patrolArea}</td>
                      <td className='center'>{item.patrolAreaNfcKey}</td>
                      <td className='center'>{item.barcodeNum}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FormIn>
            <FormBtn $Btn>
              <Btn onClick={closeModal}>Cancel</Btn>
              <Btn $Color onClick={setClient}>Submit</Btn>
            </FormBtn>
          </ModalForm>
        </div>
      </div>
    </>
  );
}

// 스타일 컴포넌트는 기존과 동일
const Title = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0 10px;

  & i {
    display: block;
    width: 4px;
    height: 32px;
    border-radius: 0 2px 2px 0;
    background: #1e1e1e;
  }

  & h4 {
    font-weight: 700;
    font-size: 18px;
    color: #000000;
  }
`;

const BtnDesc = styled.div`
  display:flex;
  align-items:center;
  gap:0 16px;

  .upload {
    position:relative;
    display:flex;
    justify-content: flex-end;
    align-items: center;

    input {
      display:none;
    }

    label {
      display:block;
      padding:0 15px;
      width:95px;
      height:40px;
      line-height:40px;
      font-weight:400;
      font-size:14px;
      color:#ffffff;
      background:#0072B9;
      cursor: pointer;
    }
  }

  .action-btn {
    display:flex;
    align-items: center;
    gap:0 16px;

    .downBtn {
      display:block;
      padding:0 15px;
      width:125px;
      height:40px;
      line-height:40px;
      font-weight:400;
      font-size:14px;
      color:#000000;
      text-align:center;
      background:#FDFD96;
    }
  }
`;

const ModalForm = styled.div`
  margin: 20px 0;
  position: relative;
  border-top: 1px solid #eeeeee;

  & > div + div {
    margin-top: 10px;
  }
`;

const FormIn = styled.div`
  display: flex;
  gap: 10px 20px;
  
  table {
    width: 100%;
    position:relative;
    
    th {
      padding:15px 0;
      background:#f4f4f4;
      font-weight:500;
      font-size:16px;
      color:#111111;
      text-align:center;
    }
    
    td {
      padding:15px 0;
      border-bottom:1px solid #dddddd;
      font-size:14px;
      color:#111111;
      vertical-align: middle;
    
      &.center {
        text-align:center;
      }
    }
  }

  & select {
    width: calc(70% - 10px);
    height: 40px;
    line-height: 40px;
    padding: 0 16px;
    background: #f8f8f8 url('https://ust-india.s3.ap-northeast-2.amazonaws.com/erp-web/sel_arrow.svg') top 50% right 10px no-repeat;
    border: 1px solid #cccccc;
    border-radius: 5px;
    outline: none;
    font-weight: 400;
    font-size: 15px;
    color: #000000;

    &:focus {
      border-color: #0072B9;
    }
  }

  .imgList {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 6px 6px;
    width: calc(100% - 33%);

    .imgBox {
      input {
        display: none;
      }

      .imgBtn {
        min-width: 90px;
        width: 90px;
        height: 90px;
        background: #efefef;
        border: 1.5px solid #d3d3d3;
        border-radius: 6px;
      }
    }

    .imgItem {
      position: relative;
      min-width: 90px;
      width: 100%;
      height: 200px;
      border-radius: 6px;
      border: 1px solid #d3d3d3;

      .imgDel {
        position: absolute;
        width: 17px;
        height: 17px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #0072b9;
        border-radius: 50%;
        top: -8px;
        right: -8px;
        cursor: pointer;
      }

      > img {
        width: 100%;
        height: 100%;
        border-radius: 6px;
      }
    }
  }
`;

const FormInWrap = styled.div`
  display: inline-flex;
  width: 50%;
  align-items: center;
  float: left;
  height: 60px;

  & label {
    font-weight: 600;
    font-size: 15px;
    color: #000000;
    width: 30%;
    margin-right: 10px;

    & span {
      font-size: 11px;
      color: red;
    }
  }

  .MuiStack-root {
    width: calc(70% - 10px) !important;

    .MuiInputBase-root {
      background: #f8f8f8;

      & input {
        height: 40px;
        background: #f8f8f8;
        padding: 0 16px;
      }
    }
  }
`;

const FormInput = styled.input`
  width: calc(70% - 10px);
  height: 40px;
  line-height: 40px;
  padding: 0 16px;
  background: #f8f8f8;
  border: 1px solid #cccccc;
  border-radius: 5px;
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  outline: none;

  &:focus {
    border-color: #0072B9;
  }
`;

const FormBtn = styled.div`
  margin-top: 25px !important;
  display: flex;
  align-items: center;
  gap: 0 8px;
`;

const Btn = styled.button`
  flex: 1;
  height: 40px;
  line-height: 40px;
  border-radius: 5px;
  background: ${props => (props['$Color'] ? '#0072b9' : '#a4a4a4')};
  font-weight: 500;
  font-size: 18px;
  color: #ffffff;
  text-align: center;
  cursor: pointer;

  &:hover {
    background-color: ${props => (props['$Color'] ? '#075C97' : '#7e7e7e')};
  }
`;

export default ManagementModal;
